import { Toast as RadixToast } from 'radix-ui';

export interface ToastProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: 'success' | 'error';
  title: string;
}

// `bg-surface-overlay` rather than `bg-white`: light mode resolves it to the same
// #FFFFFF, but the admin console renders toasts over its dark theme, where a white
// card was the one un-themed surface left. Same reason Dialog moved to tokens.
const CARD_CLASSES: Record<NonNullable<ToastProps['variant']>, string> = {
  success: 'border-green/30 bg-surface-overlay',
  error: 'border-danger/30 bg-surface-overlay',
};

const INDICATOR_CLASSES: Record<NonNullable<ToastProps['variant']>, string> = {
  success: 'bg-green text-white',
  error: 'bg-danger text-white',
};

/**
 * Single generic toast primitive (mnr-standards: "exactly one X primitive in
 * ui/" - mirrors the Dialog rule). Built on Radix Toast for a11y (aria-live
 * announce, ESC/swipe to dismiss). `Toaster` (components/common) owns the
 * Provider+Viewport and the queue; feature code never renders this directly
 * - call `toast.success`/`toast.error` (`src/lib/toast.ts`) instead.
 */
export function Toast({ open, onOpenChange, variant = 'success', title }: ToastProps) {
  return (
    <RadixToast.Root
      open={open}
      onOpenChange={onOpenChange}
      duration={5000}
      className={`rounded-card flex items-center gap-3 border p-4 shadow-lg transition-opacity duration-200 data-[state=closed]:opacity-0 data-[state=open]:opacity-100 ${CARD_CLASSES[variant]}`}
    >
      <span
        aria-hidden="true"
        className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${INDICATOR_CLASSES[variant]}`}
      >
        {variant === 'success' ? '✓' : '!'}
      </span>
      {/* text-text-primary resolves to the same navy in light mode. */}
      <RadixToast.Title className="text-text-primary text-sm font-medium">
        {title}
      </RadixToast.Title>
    </RadixToast.Root>
  );
}
